export type GuideKategorie =
  | "kaufberatung"
  | "pflegetipps"
  | "camping-guides"
  | "anfaenger"
  | "ausruestung"
  | "reiseziele";

export type GuideTag =
  | "einsteiger"
  | "fortgeschritten"
  | "pflege"
  | "winter"
  | "sommer"
  | "trekking"
  | "familie"
  | "festival"
  | "wissen"
  | "praxis";

export interface GuideAuthor {
  name: string;
  rolle: string;
  avatar?: string;
}

export interface Guide {
  id: string;
  slug: string;
  titel: string;
  beschreibung: string;
  kategorie: GuideKategorie;
  tags: GuideTag[];
  autor: GuideAuthor;
  datum: string; // ISO format
  lesezeit: number; // in Minuten
  bild: string;
  featured: boolean;
  inhalt: {
    einleitung: string;
    abschnitte: {
      ueberschrift: string;
      inhalt: string;
      liste?: string[];
      tipp?: string;
    }[];
    fazit: string;
  };
}

export const guideKategorien: { id: GuideKategorie; name: string; beschreibung: string; icon: string }[] = [
  {
    id: "kaufberatung",
    name: "Kaufberatung",
    beschreibung: "Tipps und Tricks für den Zeltkauf",
    icon: "shopping-bag",
  },
  {
    id: "pflegetipps",
    name: "Pflege & Wartung",
    beschreibung: "So bleibt Ihr Zelt lange haltbar",
    icon: "wrench",
  },
  {
    id: "camping-guides",
    name: "Camping Guides",
    beschreibung: "Praktische Anleitungen fürs Camping",
    icon: "map",
  },
  {
    id: "anfaenger",
    name: "Anfänger-Tipps",
    beschreibung: "Perfekt für Camping-Einsteiger",
    icon: "graduation-cap",
  },
  {
    id: "ausruestung",
    name: "Ausrüstung",
    beschreibung: "Was Sie neben dem Zelt brauchen",
    icon: "backpack",
  },
  {
    id: "reiseziele",
    name: "Reiseziele",
    beschreibung: "Die besten Camping-Spots",
    icon: "map-pin",
  },
];

const autoren: Record<string, GuideAuthor> = {
  max: {
    name: "Max Weber",
    rolle: "Outdoor-Experte & Zelt-Tester",
  },
  sarah: {
    name: "Sarah Müller",
    rolle: "Camping-Enthusiastin & Autorin",
  },
  tom: {
    name: "Tom Schmidt",
    rolle: "Trekking-Guide & Ausrüstungsberater",
  },
  lisa: {
    name: "Lisa Berg",
    rolle: "Outdoor-Fotografin & Bloggerin",
  },
};

export const guides: Guide[] = [
  {
    id: "1",
    slug: "zelt-kaufen-anfaenger-guide",
    titel: "Das perfekte Zelt kaufen: Der ultimative Anfänger-Guide 2025",
    beschreibung:
      "Worauf Sie beim Zeltkauf achten sollten - von der Personenanzahl über Wassersäule bis zur Saison. Ein kompletter Leitfaden für Einsteiger.",
    kategorie: "kaufberatung",
    tags: ["einsteiger", "wissen"],
    autor: autoren.max,
    datum: "2025-01-15",
    lesezeit: 22,
    bild: "https://images.unsplash.com/photo-1504280390367-361c6d9f38f4?w=1200&h=600&fit=crop",
    featured: true,
    inhalt: {
      einleitung:
        "Der Kauf eines Zeltes kann überwältigend sein - hunderte Modelle, unzählige Fachbegriffe und eine riesige Preisspanne. Dieser Guide hilft Ihnen, das perfekte Zelt für Ihre Bedürfnisse zu finden.",
      abschnitte: [
        {
          ueberschrift: "1. Personenanzahl richtig wählen",
          inhalt:
            "Die Personenangabe bei Zelten ist oft optimistisch. Ein 2-Personen-Zelt bietet Platz für zwei Personen ohne Gepäck. Für mehr Komfort sollten Sie immer eine Person mehr einplanen.",
          liste: [
            "Solo-Camper: 1-2 Personen-Zelt",
            "Paare: 3 Personen-Zelt für mehr Platz",
            "Familien: Lieber zwei kleinere als ein riesiges Zelt",
          ],
          tipp: "Planen Sie 60cm Breite pro Person ein - Sie werden es beim Schlafen zu schätzen wissen!",
        },
        {
          ueberschrift: "2. Wassersäule verstehen",
          inhalt:
            "Die Wassersäule gibt an, wie wasserdicht ein Zelt ist. Ab 1.500mm gilt ein Stoff in Deutschland als wasserdicht. Für normale Bedingungen reichen 3.000mm, für Starkregen sollten es 5.000mm oder mehr sein.",
          liste: [
            "1.500 - 3.000mm: Leichter Regen, Sommercamping",
            "3.000 - 5.000mm: Standard für die meisten Situationen",
            "5.000mm+: Extremwetter, Langzeitcamping",
          ],
        },
        {
          ueberschrift: "3. Saison-Klassifizierung",
          inhalt:
            "Zelte werden in Saisonkategorien eingeteilt, die angeben, für welche Jahreszeiten sie geeignet sind.",
          liste: [
            "2-Saison: Sommer, warme Regionen",
            "3-Saison: Frühling bis Herbst, Allround-Zelte",
            "4-Saison: Ganzjährig, auch für Winter und Schnee",
          ],
          tipp: "Für die meisten Camper in Deutschland ist ein 3-Saison-Zelt die beste Wahl!",
        },
        {
          ueberschrift: "4. Gewicht vs. Komfort",
          inhalt:
            "Trekkingzelte wiegen oft unter 2kg, bieten aber wenig Platz. Familienzelte können 15kg+ wiegen, sind dafür sehr komfortabel. Überlegen Sie, wie Sie das Zelt transportieren werden.",
        },
        {
          ueberschrift: "5. Aufbau-System",
          inhalt:
            "Moderne Zelte gibt es als Kuppelzelte, Tunnelzelte, Geodät-Zelte oder Wurfzelte. Kuppelzelte sind meist selbsttragend, Tunnelzelte bieten mehr Platz, benötigen aber Abspannungen.",
        },
      ],
      fazit:
        "Der Zeltkauf ist eine Investition. Nehmen Sie sich Zeit, vergleichen Sie Modelle und lesen Sie Bewertungen. Ein gutes Zelt hält bei richtiger Pflege viele Jahre. Nutzen Sie unseren Zelt-Vergleich, um das perfekte Modell für Ihre Bedürfnisse zu finden!",
    },
  },
  {
    id: "2",
    slug: "zelt-richtig-pflegen",
    titel: "Zelt richtig pflegen: 10 Tipps für maximale Lebensdauer",
    beschreibung:
      "Mit der richtigen Pflege hält Ihr Zelt Jahrzehnte. Erfahren Sie, wie Sie Ihr Zelt reinigen, lagern und reparieren.",
    kategorie: "pflegetipps",
    tags: ["pflege", "praxis"],
    autor: autoren.sarah,
    datum: "2025-01-10",
    lesezeit: 18,
    bild: "https://images.unsplash.com/photo-1478131143081-80f7f84ca84d?w=1200&h=600&fit=crop",
    featured: true,
    inhalt: {
      einleitung:
        "Ein Zelt ist mehr als nur Stoff und Stangen - es ist Ihr Zuhause in der Natur. Mit der richtigen Pflege bleibt es jahrelang zuverlässig und dicht.",
      abschnitte: [
        {
          ueberschrift: "1. Immer trocken lagern",
          inhalt:
            "Der wichtigste Tipp überhaupt: Lagern Sie Ihr Zelt niemals feucht oder nass! Schimmel kann sich innerhalb von Tagen bilden und das Material zerstören.",
          tipp: "Bauen Sie Ihr Zelt nach dem Camping zu Hause nochmal auf und lassen Sie es komplett durchtrocknen.",
        },
        {
          ueberschrift: "2. Richtig reinigen",
          inhalt:
            "Verwenden Sie nur lauwarmes Wasser und einen weichen Schwamm. Aggressive Reiniger oder Waschmaschinen zerstören die Imprägnierung und das Material.",
          liste: [
            "Groben Schmutz mit Bürste entfernen",
            "Mit lauwarmem Wasser abspülen",
            "Spezielle Zelt-Reiniger für hartnäckige Flecken",
            "An der Luft trocknen lassen",
          ],
        },
        {
          ueberschrift: "3. Imprägnierung erneuern",
          inhalt:
            "Nach mehreren Camping-Trips lässt die Imprägnierung nach. Wenn Wasser nicht mehr abperlt, sondern vom Stoff aufgesaugt wird, ist es Zeit für eine Nachimprägnierung.",
        },
        {
          ueberschrift: "4. Reißverschlüsse pflegen",
          inhalt:
            "Reißverschlüsse sind die häufigste Schwachstelle. Halten Sie sie sauber und behandeln Sie sie gelegentlich mit Silikonspray oder Bienenwachs.",
        },
        {
          ueberschrift: "5. Richtig lagern",
          inhalt:
            "Lagern Sie Ihr Zelt locker in einem Stoffbeutel, nicht im engen Packsack. Das schont die Imprägnierung und verhindert Knicke.",
          tipp: "Ein alter Kopfkissenbezug eignet sich perfekt als Lagerbeutel!",
        },
        {
          ueberschrift: "6. UV-Schutz beachten",
          inhalt:
            "UV-Strahlung ist Gift für Zeltstoffe. Bauen Sie Ihr Zelt wenn möglich im Schatten auf und lagern Sie es dunkel.",
        },
        {
          ueberschrift: "7. Kleine Schäden sofort reparieren",
          inhalt:
            "Ein kleines Loch wird schnell zum großen Riss. Flicken Sie kleine Schäden sofort mit Reparatur-Patches oder speziellem Nahtdichter.",
        },
        {
          ueberschrift: "8. Heringe und Gestänge checken",
          inhalt:
            "Kontrollieren Sie nach jedem Trip Heringe und Gestänge auf Beschädigungen. Verbogene Heringe richten Sie vorsichtig, beschädigte Gestängesegmente tauschen Sie aus.",
        },
        {
          ueberschrift: "9. Unterlage verwenden",
          inhalt:
            "Ein Footprint oder Tarp unter dem Zeltboden schützt vor Steinen, Wurzeln und Feuchtigkeit und verlängert die Lebensdauer erheblich.",
        },
        {
          ueberschrift: "10. Regelmäßige Inspektion",
          inhalt:
            "Einmal im Jahr sollten Sie Ihr Zelt komplett aufbauen und auf Schäden kontrollieren. So erkennen Sie Probleme, bevor Sie auf dem Campingplatz stehen.",
        },
      ],
      fazit:
        "Ein gepflegtes Zelt ist ein treuer Begleiter für viele Jahre. Die wenigen Minuten extra Aufwand nach jedem Trip zahlen sich aus - sowohl für Ihre Brieftasche als auch für zukünftige Abenteuer!",
    },
  },
  {
    id: "3",
    slug: "wintercamping-anfaenger",
    titel: "Wintercamping für Anfänger: So überstehen Sie kalte Nächte",
    beschreibung:
      "Camping bei Minusgraden? Mit der richtigen Vorbereitung ein unvergessliches Erlebnis. Alles über Winterzelte, Isolation und Sicherheit.",
    kategorie: "camping-guides",
    tags: ["winter", "anfaenger", "fortgeschritten"],
    autor: autoren.tom,
    datum: "2025-01-05",
    lesezeit: 23,
    bild: "https://images.unsplash.com/photo-1483715800462-35f1bb2d93e1?w=1200&h=600&fit=crop",
    featured: true,
    inhalt: {
      einleitung:
        "Wintercamping ist die Königsdisziplin des Outdoor-Abenteuers. Die Stille verschneiter Landschaften, glitzernde Sterne und das Knistern des Lagerfeuers bei eisigen Temperaturen machen es zu einem unvergesslichen Erlebnis. Doch Camping bei Minusgraden erfordert eine sorgfältige Vorbereitung und die richtige Ausrüstung. In diesem Guide erfahren Sie alles, was Sie für Ihre erste Winternacht im Zelt wissen müssen.",
      abschnitte: [
        {
          ueberschrift: "Das richtige Winterzelt",
          inhalt:
            "Für Wintercamping benötigen Sie ein 4-Saison-Zelt. Diese sind stabiler gebaut, haben weniger Mesh und halten Schneelasten stand.",
          liste: [
            "Stabile Gestängekonstruktion (Geodät-Design ideal)",
            "Wenig Mesh-Fläche (Wärmeverlust)",
            "Verstärkte Abspannpunkte",
            "Schneefang am Boden",
          ],
          tipp: "Ein Expeditionszelt ist Pflicht bei echtem Wintercamping - normale 3-Saison-Zelte können unter Schneelast zusammenbrechen!",
        },
        {
          ueberschrift: "Isolation ist alles",
          inhalt:
            "Der Boden ist Ihre größte Kältebrücke. Eine gute Isolationsmatte mit hohem R-Wert ist wichtiger als der teuerste Schlafsack.",
          liste: [
            "R-Wert mindestens 5, besser 6-7",
            "Zwei Matten übereinander für extreme Kälte",
            "Schlafsack: Komfortbereich unter 0°C",
            "Biwaksack für zusätzliche Isolation",
          ],
        },
        {
          ueberschrift: "Kondenswasser-Management",
          inhalt:
            "Im Winter kondensiert Ihre Atemluft im Zelt. Dieses Kondenswasser gefriert an der Innenwand und kann Sie beim Berühren nass machen.",
          tipp: "Lüften Sie regelmäßig, auch wenn es kalt ist! Nehmen Sie ein Mikrofaser-Handtuch zum Abwischen mit.",
        },
        {
          ueberschrift: "Zeltplatz-Wahl",
          inhalt:
            "Bauen Sie nicht in Senken auf (Kaltluftseen) und meiden Sie exponierte Kämme (Wind). Ideal ist ein windgeschützter Hang mit morgendlicher Sonneneinstrahlung.",
        },
        {
          ueberschrift: "Sicherheit geht vor",
          inhalt:
            "Wintercamping ist nicht zu unterschätzen. Nehmen Sie immer ein Notfall-Set mit, informieren Sie andere über Ihre Pläne und camping Sie die ersten Male nicht allein.",
          liste: [
            "Lawinenausrüstung in Bergregionen (LVS, Schaufel, Sonde)",
            "Handy + Powerbank (Akkus warm halten!)",
            "Erste-Hilfe-Set mit Kälteschutz-Produkten",
            "Ausreichend Essen (kalte Körper verbrennen mehr Kalorien)",
          ],
        },
      ],
      fazit:
        "Wintercamping ist anspruchsvoll, aber unglaublich lohnend. Beginnen Sie mit milden Winternächten nahe am Auto und steigern Sie sich langsam. Mit jeder Nacht draußen lernen Sie mehr über sich selbst und die Natur!",
    },
  },
  {
    id: "4",
    slug: "festival-camping-tipps",
    titel: "Festival-Camping: Die besten Tipps für ein entspanntes Wochenende",
    beschreibung:
      "Von der Zeltauswahl über die Platzwahl bis zu Sicherheitstipps - so wird Ihr Festival-Camping zum Erfolg.",
    kategorie: "camping-guides",
    tags: ["festival", "sommer", "einsteiger"],
    autor: autoren.lisa,
    datum: "2024-12-20",
    lesezeit: 19,
    bild: "https://images.unsplash.com/photo-1533174072545-7a4b6ad7a6c3?w=1200&h=600&fit=crop",
    featured: false,
    inhalt: {
      einleitung:
        "Festival-Camping ist eine ganz eigene Welt - anders als normales Camping, mit eigenen Regeln und Herausforderungen. Hier sind die wichtigsten Tipps für ein unvergessliches Festival-Erlebnis.",
      abschnitte: [
        {
          ueberschrift: "Das ideale Festival-Zelt",
          inhalt:
            "Ihr Festival-Zelt muss nicht teuer sein, aber robust und schnell aufzubauen. Pop-Up-Zelte sind praktisch, aber oft nicht stabil bei Wind.",
          liste: [
            "Schneller Aufbau (keine komplizierten Gestänge)",
            "Helle Farben (finden Sie es leichter wieder)",
            "Günstig (Festivals sind hart für Material)",
            "2-3 Personen für eine Person (Platz für Rucksack)",
          ],
          tipp: "Markieren Sie Ihr Zelt mit etwas Auffälligem - nach der Party finden Sie es leichter!",
        },
        {
          ueberschrift: "Die beste Platzwahl",
          inhalt:
            "Die Platzwahl kann über Freud und Leid entscheiden. Zu nah an den Stages = Lärm, zu weit = langer Weg, zu nah an Toiletten = Geruch.",
          liste: [
            "Nicht direkt neben Hauptwegen (Laufpublikum)",
            "Unter Bäumen = Schatten, aber Vogeldreck",
            "Mit Freunden zusammen = sicherer und geselliger",
            "Früh ankommen für beste Plätze",
          ],
        },
        {
          ueberschrift: "Sicherheit & Wertsachen",
          inhalt:
            "Leider gibt es auf Festivals Diebstähle. Lassen Sie nichts Wertvolles im Zelt und nutzen Sie die Schließfächer.",
          tipp: "Handy, Geld und Schlüssel immer am Körper tragen - auch nachts im Schlafsack!",
        },
        {
          ueberschrift: "Schlafen trotz Party",
          inhalt:
            "Auf Festivals wird es laut. Ohropax sind Gold wert, ebenso eine Schlafmaske. Planen Sie ein, nicht viel zu schlafen - das gehört dazu!",
        },
        {
          ueberschrift: "Hitze-Management",
          inhalt:
            "Im Sommer wird es im Zelt tagsüber unerträglich heiß. Helle Zelte heizen weniger auf. Ein Pavillon als Sonnendach ist der Luxus-Tipp!",
        },
      ],
      fazit:
        "Festival-Camping ist chaotisch, laut und manchmal unbequem - aber genau das macht den Charme aus! Mit der richtigen Vorbereitung wird es zu einem unvergesslichen Erlebnis. Viel Spaß!",
    },
  },
  {
    id: "5",
    slug: "zelt-aufbauen-tipps",
    titel: "Zelt aufbauen: Profi-Tipps für den perfekten Stand",
    beschreibung:
      "Der richtige Zeltaufbau entscheidet über eine ruhige oder stürmische Nacht. Lernen Sie die wichtigsten Techniken.",
    kategorie: "anfaenger",
    tags: ["einsteiger", "praxis", "wissen"],
    autor: autoren.max,
    datum: "2024-12-15",
    lesezeit: 17,
    bild: "https://images.unsplash.com/photo-1445308394109-4ec2920981b1?w=1200&h=600&fit=crop",
    featured: false,
    inhalt: {
      einleitung:
        "Ein richtig aufgebautes Zelt steht stabil, ist wasserdicht und bietet optimalen Komfort. Diese Tipps helfen Ihnen, Ihr Zelt perfekt aufzustellen.",
      abschnitte: [
        {
          ueberschrift: "Den richtigen Platz finden",
          inhalt:
            "Vor dem Aufbau kommt die Platzwahl. Räumen Sie Steine, Äste und Unebenheiten weg. Achten Sie auf die Windrichtung - öffnen Sie das Zelt nicht in Hauptwindrichtung.",
          liste: [
            "Ebener, fester Untergrund",
            "Keine Senke (Wasseransammlung bei Regen)",
            "Ausreichend Abspannfläche rundherum",
            "Bei Hanglage: Kopf bergauf",
          ],
          tipp: "Legen Sie sich kurz hin und testen Sie den Platz - Unebenheiten spüren Sie sofort!",
        },
        {
          ueberschrift: "Die richtige Reihenfolge",
          inhalt:
            "Die meisten Fehler passieren durch falsche Aufbau-Reihenfolge. Lesen Sie die Anleitung vorher - am besten zu Hause in Ruhe.",
          liste: [
            "1. Unterlage/Footprint auslegen",
            "2. Innenzelt ausbreiten",
            "3. Gestänge zusammenstecken und einfädeln",
            "4. Zelt aufstellen und fixieren",
            "5. Außenzelt überwerfen und befestigen",
            "6. Abspannen und nachjustieren",
          ],
        },
        {
          ueberschrift: "Richtig abspannen",
          inhalt:
            "Abspannleinen sind keine Dekoration! Sie stabilisieren das Zelt bei Wind und sorgen für Spannung im Außenzelt (verhindert Kondenswasser-Kontakt).",
          tipp: "Spannen Sie Leinen in einem 45°-Winkel ab, nicht flach oder zu steil. Verwenden Sie Reflektorleinen oder markieren Sie sie mit Klebeband - Stolperfalle!",
        },
        {
          ueberschrift: "Heringe richtig setzen",
          inhalt:
            "Heringe gehören schräg in den Boden (ca. 45-60° vom Zelt weg). So halten sie der Zugkraft besser stand. In lockeren Böden nutzen Sie Sandheringe, in harten Böden Stahlheringe.",
        },
        {
          ueberschrift: "Erste Nacht-Checks",
          inhalt:
            "Nach dem Aufbau: Kontrollieren Sie alle Reißverschlüsse, tasten Sie von innen die Nähte ab, testen Sie die Spannung. Lieber jetzt nachjustieren als nachts im Regen!",
        },
      ],
      fazit:
        "Ein gut aufgebautes Zelt ist wie ein gutes Fundament - man merkt es erst, wenn etwas schief geht. Nehmen Sie sich Zeit für den Aufbau und Ihr Zelt wird Sie mit ruhigen Nächten belohnen!",
    },
  },
  {
    id: "6",
    slug: "trekking-zelt-auswahl",
    titel: "Trekkingzelt kaufen: Leicht, robust und zuverlässig",
    beschreibung:
      "Was macht ein gutes Trekkingzelt aus? Gewicht, Packmaß, Wetterbeständigkeit - der komplette Guide für Weitwanderer.",
    kategorie: "kaufberatung",
    tags: ["trekking", "fortgeschritten", "wissen"],
    autor: autoren.tom,
    datum: "2024-12-10",
    lesezeit: 21,
    bild: "https://images.unsplash.com/photo-1537905569824-f89f14cceb68?w=1200&h=600&fit=crop",
    featured: false,
    inhalt: {
      einleitung:
        "Beim Trekking zählt jedes Gramm. Ein gutes Trekkingzelt muss leicht sein, aber trotzdem Schutz und Komfort bieten. Hier erfahren Sie, worauf es ankommt.",
      abschnitte: [
        {
          ueberschrift: "Das Gewichts-Dilemma",
          inhalt:
            "Ultraleicht-Zelte wiegen unter 1kg, bieten aber wenig Komfort. Robuste Trekkingzelte wiegen 2-3kg, halten aber mehr aus. Finden Sie Ihre Balance zwischen Gewicht und Komfort.",
          liste: [
            "Ultraleicht (<1kg): Nur für erfahrene Minimalist-Trekker",
            "Leicht (1-2kg): Guter Kompromiss für die meisten",
            "Standard (2-3kg): Robust und komfortabel, für lange Touren",
          ],
          tipp: "Teilen Sie bei Duo-Trekking: Eine Person trägt Innenzelt, die andere Außenzelt und Gestänge!",
        },
        {
          ueberschrift: "Freistehend vs. Abgespannt",
          inhalt:
            "Freistehende Zelte (Kuppelzelte) sind flexibel beim Standort, wiegen aber mehr. Trekkingstöcke-Zelte sind ultra-leicht, brauchen aber guten Boden für Heringe.",
        },
        {
          ueberschrift: "Einwand vs. Doppelwand",
          inhalt:
            "Einwandzelte sind leichter, haben aber mehr Kondenswasser-Probleme. Doppelwandzelte sind schwerer, aber komfortabler und wetterbeständiger.",
        },
        {
          ueberschrift: "Packmaß ist wichtig",
          inhalt:
            "Das Packmaß muss in oder an Ihren Rucksack passen. Messen Sie vorher! Lange, schmale Packsäcke lassen sich besser verstauen als kurze, dicke.",
        },
        {
          ueberschrift: "Apsis für Ausrüstung",
          inhalt:
            "Eine oder zwei Apsiden (Vorbau) sind Gold wert. Hier lagern Sie Rucksack, Schuhe und Kochausrüstung trocken und getrennt vom Schlafbereich.",
          tipp: "Bei Solotouren reicht eine Apsis, zu zweit sind zwei Apsiden deutlich komfortabler!",
        },
      ],
      fazit:
        "Das perfekte Trekkingzelt gibt es nicht - nur das perfekte für Ihre Bedürfnisse. Überlegen Sie, wo und wie Sie trekken werden, und wählen Sie entsprechend. Qualität zahlt sich beim Trekking besonders aus!",
    },
  },
  {
    id: "7",
    slug: "familienzelt-kaufen",
    titel: "Familienzelt kaufen: Platz, Komfort und Familientauglichkeit",
    beschreibung:
      "Mit Kindern campen ist wunderbar - wenn das Zelt stimmt. Was ein gutes Familienzelt ausmacht und worauf Sie achten sollten.",
    kategorie: "kaufberatung",
    tags: ["familie", "einsteiger", "wissen"],
    autor: autoren.sarah,
    datum: "2024-12-05",
    lesezeit: 20,
    bild: "https://images.unsplash.com/photo-1478131143081-80f7f84ca84d?w=1200&h=600&fit=crop",
    featured: false,
    inhalt: {
      einleitung:
        "Camping mit der Familie ist Abenteuer und Erholung zugleich. Das richtige Familienzelt macht den Unterschied zwischen Chaos und Entspannung. Hier ist Ihr Kaufguide.",
      abschnitte: [
        {
          ueberschrift: "Größe richtig planen",
          inhalt:
            "Bei Familienzelten gilt: Lieber zu groß als zu klein. Planen Sie pro Person 2m² Grundfläche plus Stauraum für Gepäck und Spielzeug ein.",
          liste: [
            "4-Personen-Familie: Minimum 6-Personen-Zelt",
            "Kabinen für Privatsphäre",
            "Stehhöhe ist Luxus, den Sie zu schätzen wissen",
            "Großer Vorraum als Wohn- und Essbereich",
          ],
          tipp: "Kinder wachsen schnell - kaufen Sie das Zelt eine Nummer größer, es hält viele Jahre!",
        },
        {
          ueberschrift: "Aufbau-Freundlichkeit",
          inhalt:
            "Mit Kindern haben Sie keine Stunde Zeit für komplizierte Aufbauten. Tunnelzelte mit farbcodierten Gestängen oder moderne Quick-Up-Systeme sind ideal.",
        },
        {
          ueberschrift: "Materialien & Qualität",
          inhalt:
            "Familienzelte werden strapaziert. Robuste Böden (mindestens 5.000mm Wassersäule), verstärkte Nähte und hochwertige Reißverschlüsse sind Pflicht.",
        },
        {
          ueberschrift: "Belüftung & Fenster",
          inhalt:
            "Gute Belüftung verhindert Kondenswasser und hält das Zelt im Sommer kühl. Moskitonetze an allen Öffnungen sind essenziell. Durchsichtige Fenster geben Kindern ein sicheres Gefühl.",
        },
        {
          ueberschrift: "Extras für Familien",
          inhalt:
            "Kabeltülle für Stromkabel, Lampenhaken, Innentaschen für Kleinkram - diese Details machen das Camping mit Familie deutlich angenehmer.",
          liste: [
            "Verdunkelbare Schlafkabinen (Kinder schlafen länger!)",
            "Abnehmbare Kabinen-Trennwände",
            "Klarsicht-Fenster in Kabinen",
            "Integrierte Organizer und Taschen",
          ],
        },
      ],
      fazit:
        "Ein gutes Familienzelt ist eine Investition in unvergessliche Familien-Erlebnisse. Nehmen Sie sich Zeit bei der Auswahl und testen Sie den Aufbau vorher im Garten - das erste Mal mit Kindern sollte nicht auf dem Campingplatz sein!",
    },
  },
  {
    id: "8",
    slug: "zelt-impragnieren-anleitung",
    titel: "Zelt imprägnieren: Schritt-für-Schritt Anleitung",
    beschreibung:
      "Wann und wie Sie Ihr Zelt nachimprägnieren sollten. Mit dieser Anleitung bleibt Ihr Zelt jahrelang wasserdicht.",
    kategorie: "pflegetipps",
    tags: ["pflege", "praxis"],
    autor: autoren.max,
    datum: "2024-11-28",
    lesezeit: 18,
    bild: "https://images.unsplash.com/photo-1504280390367-361c6d9f38f4?w=1200&h=600&fit=crop",
    featured: false,
    inhalt: {
      einleitung:
        "Jede Imprägnierung lässt mit der Zeit nach - durch UV-Strahlung, Reibung und Wäsche. Wann es Zeit für eine Nachbehandlung ist und wie Sie richtig imprägnieren, erfahren Sie hier.",
      abschnitte: [
        {
          ueberschrift: "Wann nachimprägnieren?",
          inhalt:
            "Der Wasserperlentest zeigt es: Sprühen Sie Wasser aufs Zelt. Perlt es ab, ist die Imprägnierung OK. Saugt der Stoff das Wasser auf, ist Nachbehandlung nötig.",
          liste: [
            "Nach ca. 20-30 Camping-Tagen",
            "Wenn Wasser nicht mehr abperlt",
            "Nach jeder Zelt-Wäsche",
            "Bei sichtbarem Abrieb (Gestänge-Kontaktstellen)",
          ],
          tipp: "Testen Sie mehrere Stellen - Abrieb tritt oft zuerst an Gestänge-Kontaktpunkten auf!",
        },
        {
          ueberschrift: "Die richtigen Produkte",
          inhalt:
            "Für Zelte gibt es spezielle Imprägniermittel - meist auf Silikon- oder PU-Basis. Verwenden Sie NIEMALS Nikwax oder ähnliche Produkte für Kleidung - diese können die Beschichtung beschädigen!",
          liste: [
            "Außenzelt: Spray oder Einwasch-Imprägnierung",
            "Nähte: Nahtdichter (Seam Sealer)",
            "Zeltboden: Spezielle Boden-Versiegelung",
          ],
        },
        {
          ueberschrift: "Schritt-für-Schritt Anleitung",
          inhalt:
            "1. Zelt gründlich reinigen und trocknen lassen. 2. Im Freien oder gut belüfteten Raum aufbauen. 3. Spray gleichmäßig auftragen oder Einwasch-Mittel verwenden. 4. Trocknen lassen (mindestens 24h). 5. Wassertest durchführen.",
          tipp: "Tragen Sie Handschuhe und achten Sie auf gute Belüftung - Imprägniermittel enthalten Lösungsmittel!",
        },
        {
          ueberschrift: "Nähte extra behandeln",
          inhalt:
            "Nähte sind Schwachstellen. Nach dem Imprägnieren sollten Sie alle Nähte mit Nahtdichter nacharbeiten - besonders bei älteren Zelten.",
        },
        {
          ueberschrift: "Häufige Fehler vermeiden",
          inhalt:
            "Zu viel Imprägniermittel schadet mehr als es nützt. Die Atmungsaktivität leidet und der Stoff wird starr. Dünne, gleichmäßige Schichten sind besser als eine dicke Beschichtung.",
        },
      ],
      fazit:
        "Eine regelmäßige Nachimprägnierung ist wie Öl nachfüllen beim Auto - eine kleine Wartung, die große Schäden verhindert. Nehmen Sie sich die Zeit dafür, und Ihr Zelt bleibt jahrelang zuverlässig dicht!",
    },
  },
  {
    id: "9",
    slug: "nachhaltig-campen",
    titel: "Nachhaltig campen: Leave No Trace Prinzipien",
    beschreibung:
      "Camping und Naturschutz gehören zusammen. Lernen Sie die Leave No Trace Prinzipien und campen Sie umweltfreundlich.",
    kategorie: "camping-guides",
    tags: ["wissen", "einsteiger", "praxis"],
    autor: autoren.lisa,
    datum: "2024-11-20",
    lesezeit: 19,
    bild: "https://images.unsplash.com/photo-1478131143081-80f7f84ca84d?w=1200&h=600&fit=crop",
    featured: false,
    inhalt: {
      einleitung:
        "Wir lieben die Natur - deshalb sollten wir sie schützen. Die Leave No Trace Prinzipien helfen uns, beim Camping minimale Spuren zu hinterlassen und die Natur für kommende Generationen zu erhalten.",
      abschnitte: [
        {
          ueberschrift: "1. Gute Planung und Vorbereitung",
          inhalt:
            "Nachhaltiges Camping beginnt vor der Abfahrt. Informieren Sie sich über Regeln, packen Sie Mehrweg statt Einweg ein und planen Sie Ihre Route.",
          liste: [
            "Camping nur auf ausgewiesenen Plätzen",
            "Mehrweg-Behälter statt Einweg-Verpackungen",
            "Regionale Lebensmittel einkaufen",
            "Müllsäcke einpacken",
          ],
        },
        {
          ueberschrift: "2. Robuste Oberflächen nutzen",
          inhalt:
            "Campen Sie auf bereits genutzten Flächen oder robusten Untergründen wie Kies, trockenem Gras oder Waldboden. Vermeiden Sie empfindliche Vegetation, Feuchtgebiete und Uferböden.",
          tipp: "Wenn Sie off-trail campen müssen: Bleiben Sie maximal eine Nacht und verteilen Sie Ihre Gruppe, um Trampelpfade zu vermeiden!",
        },
        {
          ueberschrift: "3. Abfall richtig entsorgen",
          inhalt:
            "Pack it in, pack it out - alles was Sie mitbringen, nehmen Sie wieder mit. Das gilt auch für Bananenschalen, Apfelbutzen und 'biologischen' Müll!",
          liste: [
            "Mülltrennung auch beim Camping",
            "Essensreste nicht in die Natur werfen",
            "Seife und Spülmittel biologisch abbaubar",
            "Toilette: Min. 70m von Gewässern entfernt",
          ],
        },
        {
          ueberschrift: "4. Respekt vor Wildtieren",
          inhalt:
            "Beobachten Sie Tiere aus der Distanz, füttern Sie niemals Wildtiere und lagern Sie Essensreste sicher. Ihr Essen kann Tiere krank machen oder ihr Verhalten verändern.",
        },
        {
          ueberschrift: "5. Lagerfeuer mit Bedacht",
          inhalt:
            "Lagerfeuer sind gemütlich, aber nicht immer erlaubt oder sinnvoll. Nutzen Sie vorhandene Feuerstellen, halten Sie Feuer klein und löschen Sie gründlich.",
          tipp: "Ein Gaskocher ist effizienter, sicherer und hinterlässt keine Spuren - überlegen Sie, ob ein Lagerfeuer wirklich nötig ist!",
        },
        {
          ueberschrift: "6. Rücksicht auf andere",
          inhalt:
            "Halten Sie Lärm minimal, respektieren Sie andere Camper und Wanderer, und teilen Sie beliebte Spots. Die Natur gehört uns allen.",
        },
      ],
      fazit:
        "Nachhaltiges Camping bedeutet nicht Verzicht - es bedeutet bewusstes Genießen. Mit diesen Prinzipien stellen Sie sicher, dass Ihre Kinder und Enkel dieselbe unberührte Natur erleben können wie Sie heute. Leave nothing but footprints, take nothing but memories!",
    },
  },
  {
    id: "10",
    slug: "camping-packliste",
    titel: "Die ultimative Camping-Packliste 2025",
    beschreibung:
      "Nie wieder etwas vergessen! Unsere komplette Packliste für Camping-Trips - von der Grundausrüstung bis zu den versteckten Helfern.",
    kategorie: "ausruestung",
    tags: ["einsteiger", "praxis", "wissen"],
    autor: autoren.sarah,
    datum: "2024-11-15",
    lesezeit: 23,
    bild: "https://images.unsplash.com/photo-1478131143081-80f7f84ca84d?w=1200&h=600&fit=crop",
    featured: false,
    inhalt: {
      einleitung:
        "Die richtige Packliste ist Gold wert. Sie verhindert, dass Sie auf dem Campingplatz feststellen, dass Sie etwas Wichtiges vergessen haben. Hier ist unsere ultimative Liste - von essenziell bis 'nice to have'.",
      abschnitte: [
        {
          ueberschrift: "Zelt & Schlafen (Essentiell)",
          inhalt: "Die Basis für jedes Camping-Abenteuer.",
          liste: [
            "Zelt mit Heringen und Abspannleinen",
            "Unterlage/Footprint",
            "Schlafsack (passend zur Jahreszeit)",
            "Isomatte oder Luftmatratze",
            "Kissen (oder Kleidersack als Ersatz)",
            "Reparatur-Set für Zelt und Isomatte",
          ],
          tipp: "Testen Sie Ihr Zelt vorher im Garten - nichts ist schlimmer als ein fehlendes Teil auf dem Campingplatz!",
        },
        {
          ueberschrift: "Küche & Verpflegung",
          inhalt: "Essen und Trinken ist wichtig - und macht mit der richtigen Ausrüstung sogar Spaß!",
          liste: [
            "Campingkocher mit Gaskartusche(n)",
            "Kochgeschirr (Topf, Pfanne)",
            "Besteck und Geschirr (Mehrweg!)",
            "Schneidbrett und Messer",
            "Kühlbox oder Kühltasche",
            "Wasserbehälter/Kanister",
            "Spülmittel (biologisch abbaubar) und Schwamm",
            "Müllbeutel",
            "Lebensmittel + Gewürze",
            "Kaffee/Tee + Becher",
          ],
        },
        {
          ueberschrift: "Kleidung (Je nach Saison)",
          inhalt:
            "Zwiebelprinzip ist der Schlüssel! Mehrere dünne Schichten sind besser als eine dicke Jacke.",
          liste: [
            "Funktionsunterwäsche",
            "Wandersocken (Ersatzpaar!)",
            "Wander-/Camping-Hose",
            "T-Shirts / Funktionsshirts",
            "Fleece oder Pullover",
            "Regen-/Windjacke",
            "Warme Jacke (Abends)",
            "Kopfbedeckung (Sonne/Kälte)",
            "Feste Schuhe + Campingschuhe",
            "Badekleidung",
          ],
        },
        {
          ueberschrift: "Hygiene & Gesundheit",
          inhalt: "Auch outdoor bleiben wir zivilisiert!",
          liste: [
            "Zahnbürste & Zahnpasta",
            "Seife/Duschgel (biologisch abbaubar)",
            "Handtuch (Mikrofaser trocknet schnell)",
            "Toilettenpapier",
            "Sonnencreme",
            "Mückenschutz",
            "Erste-Hilfe-Set",
            "Persönliche Medikamente",
            "Tampons/Binden",
          ],
        },
        {
          ueberschrift: "Licht & Elektronik",
          inhalt: "Wenn die Sonne untergeht, sind Sie dankbar für gutes Licht!",
          liste: [
            "Stirnlampe oder Taschenlampe",
            "Ersatzbatterien",
            "Campinglampe für Zelt",
            "Handy + Ladekabel",
            "Powerbank",
            "Steckdosenleiste (bei Stromanschluss)",
          ],
        },
        {
          ueberschrift: "Tools & Hilfsmittel",
          inhalt: "Die kleinen Helfer, die Sie immer wieder brauchen werden.",
          liste: [
            "Taschenmesser oder Multitool",
            "Feuerzeug/Streichhölzer (wasserdicht)",
            "Schnur/Paracord",
            "Klebeband (Duct Tape)",
            "Wäscheleine und Klammern",
            "Taschen für Wertsachen",
            "Campingstuhl",
            "Campingtisch",
          ],
          tipp: "Duct Tape ist der Freund jedes Campers - von Reparaturen bis zur Improvisation!",
        },
        {
          ueberschrift: "Nice-to-Have (Komfort)",
          inhalt: "Kein Muss, aber deutlich angenehmer!",
          liste: [
            "Buch oder E-Reader",
            "Karten-/Würfelspiel",
            "Bluetooth-Lautsprecher",
            "Hängematte",
            "Campingdusche",
            "Sonnensegel/Pavillon",
            "Sitzkissen",
            "Fernglas",
          ],
        },
        {
          ueberschrift: "Dokumente & Geld",
          inhalt: "Nicht vergessen!",
          liste: [
            "Personalausweis/Reisepass",
            "Krankenversicherungskarte",
            "Campingplatz-Buchungsbestätigung",
            "Bargeld + EC-Karte",
            "Versicherungsunterlagen",
          ],
        },
      ],
      fazit:
        "Diese Liste ist umfangreich - passen Sie sie an Ihre Bedürfnisse an! Bei Autocamping können Sie mehr mitnehmen, beim Trekking zählt jedes Gramm. Machen Sie sich eine persönliche Checkliste und haken Sie beim Packen ab. So vergessen Sie nichts Wichtiges und können Ihr Camping-Abenteuer entspannt genießen!",
    },
  },
];

// Filter & Utility Functions
export function filterGuides(options: {
  kategorie?: GuideKategorie;
  tags?: GuideTag[];
  suche?: string;
}): Guide[] {
  let result = [...guides];

  if (options.kategorie) {
    result = result.filter(g => g.kategorie === options.kategorie);
  }

  if (options.tags && options.tags.length > 0) {
    result = result.filter(g => options.tags!.some(tag => g.tags.includes(tag)));
  }

  if (options.suche) {
    const search = options.suche.toLowerCase();
    result = result.filter(
      g =>
        g.titel.toLowerCase().includes(search) ||
        g.beschreibung.toLowerCase().includes(search) ||
        g.inhalt.einleitung.toLowerCase().includes(search)
    );
  }

  return result;
}

export function sortGuides(
  list: Guide[],
  sortBy: "datum" | "lesezeit" | "titel" = "datum",
  order: "asc" | "desc" = "desc"
): Guide[] {
  const sorted = [...list].sort((a, b) => {
    let aVal: string | number;
    let bVal: string | number;

    switch (sortBy) {
      case "datum":
        aVal = new Date(a.datum).getTime();
        bVal = new Date(b.datum).getTime();
        break;
      case "lesezeit":
        aVal = a.lesezeit;
        bVal = b.lesezeit;
        break;
      case "titel":
        aVal = a.titel;
        bVal = b.titel;
        break;
      default:
        return 0;
    }

    if (aVal < bVal) return order === "asc" ? -1 : 1;
    if (aVal > bVal) return order === "asc" ? 1 : -1;
    return 0;
  });

  return sorted;
}

export function getFeaturedGuides(limit = 3): Guide[] {
  return guides.filter(g => g.featured).slice(0, limit);
}

export function getRelatedGuides(guide: Guide, limit = 3): Guide[] {
  // Find guides with same category or overlapping tags, excluding current guide
  const related = guides
    .filter(g => g.id !== guide.id)
    .map(g => {
      let score = 0;
      if (g.kategorie === guide.kategorie) score += 3;
      const commonTags = g.tags.filter(tag => guide.tags.includes(tag)).length;
      score += commonTags;
      return { guide: g, score };
    })
    .filter(item => item.score > 0)
    .sort((a, b) => b.score - a.score)
    .slice(0, limit)
    .map(item => item.guide);

  return related;
}

export function formatDate(isoDate: string): string {
  const date = new Date(isoDate);
  return date.toLocaleDateString("de-DE", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
}
